import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Sparkles, Camera,
  Image as ImageIcon, Eye,
} from 'lucide-react';
import Shell from '../components/TopBarShell';
import NewsletterPolaroid from '../components/NewsletterPolaroid';
import api from '../utils/api';
import { useLocale, useT } from '../contexts/LocaleProvider';
import { LT } from '../lib/appCatalogue';
import { localizedEpisode, localizedSection } from '../lib/newsletterLocale';

/* MANEVA newsletters · the internal communications hub.
 *
 *   /resources/newsletters             → home (intro + latest + archive
 *                                        + polaroid + spotlight + events)
 *   /resources/newsletters/<slug>      → single episode in long-form
 *
 * As of the CMS refactor, all content is API-driven. Episodes live in
 * the `Newsletter` model with ordered `NewsletterSection` rows; the
 * `<SectionRenderer>` dispatches each section to one of the shared
 * helper components (Section / P / H3 / Bullets / Gallery / Clue /
 * FinTable). HR authors via /admin/newsletters; the renderer side
 * stays stable. */

export default function Newsletters() {
  const { slug } = useParams();
  if (slug) return <NewsletterEpisode slug={slug} />;
  return <NewslettersHome />;
}

/* ─────────────────────────────────────────────────────────────────
 * Newsletter hub · landing page · API-driven archive + latest + the
 * polaroid camera + a spotlight slot (video / image / text) + the
 * "prochains événements" rail. The spotlight is currently a static
 * widget — backed by a CMS model in a follow-up.
 * ──────────────────────────────────────────────────────────────── */
const NewslettersHome = () => {
  const t = useT();
  const { locale } = useLocale();
  const [episodes, setEpisodes] = useState(null);   // null = loading
  useEffect(() => {
    api.newsletters({ locale })
      .then(rows => setEpisodes(Array.isArray(rows) ? rows : (rows?.results || [])))
      .catch(() => setEpisodes([]));
  }, [locale]);

  const latest = episodes && episodes[0];
  const rest   = episodes ? episodes.slice(1) : [];

  return (
    <Shell>
      <header className="mb-8 max-w-[820px]">
        <div className="text-[11px] uppercase tracking-[0.16em] font-medium" style={{ color: LT.green50 }}>
          {t('maneva.eyebrow')}
        </div>
        <h1 className="font-display text-[2rem] sm:text-[2.5rem] leading-tight tracking-[-0.02em] mt-2"
            style={{ color: LT.greenDeep }}>
          {t('maneva.home.title')}
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed" style={{ color: '#3a4f44' }}>
          {t('maneva.home.intro')}
        </p>
      </header>

      {/* À la une · latest issue, two-column · each episode carries
          its own cover (HR uploads it in the editor). When missing,
          a brand-colored placeholder with the episode numeral fills
          the slot so the layout never goes empty. */}
      {latest && (
        <section className="mb-8 max-w-[1100px]">
          <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-[0.14em] font-medium"
               style={{ color: LT.green50 }}>
            <Sparkles className="h-3 w-3" /> {t('maneva.featured')}
          </div>
          <Link to={`/resources/newsletters/${latest.slug}`}
                className="group block rounded-2xl overflow-hidden transition hover:-translate-y-px"
                style={{
                  background: LT.beigeLight,
                  border: `1px solid ${LT.green30}`,
                  borderTop: `4px solid ${LT.yellow}`,
                  boxShadow: '0 1px 0 rgba(43,65,56,0.04), 0 6px 18px rgba(43,65,56,0.04)',
                }}>
            <div className="grid grid-cols-1 md:grid-cols-5">
              <div className="md:col-span-2 aspect-[4/3] md:aspect-auto overflow-hidden">
                <EpisodeHero ep={latest} variant="large" />
              </div>
              <div className="md:col-span-3 p-7 sm:p-9 flex flex-col gap-3">
                {(() => {
                  const l = localizedEpisode(latest, locale);
                  return (
                    <>
                      <div className="text-[10.5px] uppercase tracking-[0.16em] font-medium font-num"
                           style={{ color: LT.green50 }}>
                        {t('maneva.episode')} {latest.number} · {l.date_label}
                      </div>
                      <h2 className="font-display text-[1.5rem] sm:text-[1.85rem] leading-[1.1] tracking-tight"
                          style={{ color: LT.greenDeep }}>
                        {l.title}
                      </h2>
                      <p className="text-[14.5px] leading-relaxed" style={{ color: '#3a4f44' }}>
                        {l.teaser}
                      </p>
                    </>
                  );
                })()}
                <div className="mt-1 inline-flex items-center gap-1.5 text-[13px] font-medium self-start"
                     style={{ color: LT.greenDeep }}>
                  {t('maneva.read')} <ArrowRight className="h-3.5 w-3.5" />
                </div>
              </div>
            </div>
          </Link>
        </section>
      )}

      {/* Spotlight + Polaroid row · editorial video on the left
          (drop replacements at public/newsletters/spotlight/
          spotlight.mp4), rotating polaroid album on the right. */}
      <section className="mb-8 max-w-[1100px] grid grid-cols-1 lg:grid-cols-3 gap-5 items-stretch">
        <div className="lg:col-span-2 h-full">
          <Spotlight />
        </div>
        <div className="h-full rounded-2xl p-6 flex flex-col items-center"
             style={{ background: 'rgba(251,251,250,0.55)',
                      boxShadow: '0 1px 0 rgba(43,65,56,0.04), 0 4px 14px rgba(43,65,56,0.04)' }}>
          <div className="self-start flex items-center gap-2 mb-3 text-[11px] uppercase tracking-[0.14em] font-medium"
               style={{ color: LT.green50 }}>
            <Camera className="h-3 w-3" /> {t('maneva.album')}
          </div>
          <NewsletterPolaroid mode="full" />
        </div>
      </section>

      {/* Archive · full-width since 'Prochains événements' moved up
          to the header rail. Items use a borderless, subtly-lifted
          card so the row reads as a list, not a stack of boxes. */}
      <section className="mb-10 max-w-[1100px]">
        <div className="text-[11px] uppercase tracking-[0.14em] font-medium mb-3"
             style={{ color: LT.green50 }}>
          {t('maneva.archive')}
        </div>
        {episodes === null && <ArchiveSkeleton />}
        {rest.length === 0 && episodes !== null && (
          <div className="rounded-2xl p-6 text-[14px]"
               style={{ background: 'rgba(251,251,250,0.55)', color: LT.green50 }}>
            {t('maneva.archive.empty')}
          </div>
        )}
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {rest.map(ep => {
            const l = localizedEpisode(ep, locale);
            return (
              <li key={ep.slug}>
                <Link to={`/resources/newsletters/${ep.slug}`}
                      className="group flex gap-4 p-4 rounded-2xl transition hover:-translate-y-px"
                      style={{ background: 'rgba(251,251,250,0.55)',
                               boxShadow: '0 1px 0 rgba(43,65,56,0.04), 0 4px 14px rgba(43,65,56,0.04)' }}>
                  <div className="w-24 h-24 shrink-0 rounded-xl overflow-hidden">
                    <EpisodeHero ep={ep} variant="thumb" />
                  </div>
                  <div className="min-w-0 flex flex-col gap-1.5">
                    <div className="text-[10.5px] uppercase tracking-[0.14em] font-num"
                         style={{ color: LT.green50 }}>
                      {t('maneva.episode')} {ep.number} · {l.date_label}
                    </div>
                    <div className="font-display text-[1.05rem] leading-tight"
                         style={{ color: LT.greenDeep }}>
                      {l.title}
                    </div>
                    <p className="text-[12.5px] leading-relaxed line-clamp-2" style={{ color: '#3a4f44' }}>
                      {l.teaser}
                    </p>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </section>
    </Shell>
  );
};

/* Spotlight · plays the editorial-moment clip dropped at
 * public/newsletters/spotlight/spotlight.mp4. No chrome around the
 * video itself · the eyebrow + title that used to sit above it were
 * removed so the clip leads. Replace the file to swap the clip. */
const Spotlight = () => {
  const t = useT();
  // The clip comes from the CMS (photos admin · « Vidéo à la une ») and
  // falls back to the one shipped with the frontend while none is set.
  const [spot, setSpot] = useState(undefined);   // undefined = loading, null = none
  useEffect(() => {
    api.newsletterSpotlight().then(s => setSpot(s || null)).catch(() => setSpot(null));
  }, []);
  const videoSrc = spot?.video || '/newsletters/spotlight/spotlight.mp4';
  const [errored, setErrored] = useState(false);
  if (spot === undefined) {
    return <div className="h-full min-h-[240px] rounded-2xl animate-pulse" style={{ background: 'rgba(251,251,250,0.55)' }} />;
  }
  if (errored) {
    return (
      <div className="aspect-video w-full rounded-xl bg-[hsl(var(--bg-subtle))] flex flex-col items-center justify-center gap-2"
           style={{ color: LT.green50 }}>
        <ImageIcon className="h-7 w-7" />
        <div className="text-[11.5px] uppercase tracking-[0.14em] font-medium">
          {t('maneva.spotlight.empty')}
        </div>
      </div>
    );
  }
  return (
    // Wrap in the same rounded panel as the polaroid box next to it
    // so the row reads as a coherent card grid. `h-full` lets the
    // wrapper stretch to match the polaroid panel's row height;
    // `object-cover` makes the clip fill the panel cleanly so the
    // two blocks are flush instead of one having translucent slack.
    <div className="h-full rounded-2xl overflow-hidden"
         style={{ background: 'rgba(251,251,250,0.55)',
                  boxShadow: '0 1px 0 rgba(43,65,56,0.04), 0 4px 14px rgba(43,65,56,0.04)' }}>
      <video
        key={videoSrc}
        src={videoSrc}
        poster={spot?.poster || undefined}
        className="block w-full h-full object-cover"
        autoPlay loop muted playsInline
        onError={() => setErrored(true)}
      />
    </div>
  );
};

/* « Previous / next episode » at the foot of an episode · neighbours by
 * number among published episodes, handed over by the API. */
const NeighborLink = ({ ep, dir, locale, t }) => {
  const l = localizedEpisode(ep, locale);
  const older = dir === 'older';
  return (
    <Link to={`/resources/newsletters/${ep.slug}`}
          className={`group flex flex-col gap-1 p-4 rounded-2xl transition hover:-translate-y-px ${older ? '' : 'sm:text-right sm:items-end'}`}
          style={{ background: 'rgba(251,251,250,0.55)',
                   boxShadow: '0 1px 0 rgba(43,65,56,0.04), 0 4px 14px rgba(43,65,56,0.04)' }}>
      <span className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.14em] font-medium"
            style={{ color: LT.green50 }}>
        {older && <ArrowLeft className="h-3 w-3" />}
        {t(older ? 'maneva.older' : 'maneva.newer')}
        {!older && <ArrowRight className="h-3 w-3" />}
      </span>
      <span className="font-display text-[1.05rem] leading-tight" style={{ color: LT.greenDeep }}>
        {t('maneva.episode')} {ep.number} · {l.title}
      </span>
    </Link>
  );
};

/* Episode hero · one component for both the À la une card and the
 * archive thumbnails. If the episode has a `cover` upload, it fills
 * the slot; otherwise a brand-colored block with the numeral keeps
 * the layout intentional instead of empty. */
const EpisodeHero = ({ ep, variant }) => {
  const t = useT();
  if (ep?.cover) {
    return (
      <img src={ep.cover} alt=""
           className="w-full h-full object-cover transition group-hover:scale-[1.01]"
           draggable={false} />
    );
  }
  const big = variant === 'large';
  return (
    <div className="w-full h-full flex flex-col items-center justify-center relative"
         style={{ background: LT.greenDeep, color: LT.yellow }}>
      <span className="font-num text-[10px] uppercase tracking-[0.18em] opacity-70"
            style={{ color: '#fefefe' }}>
        {t('maneva.placeholder.eyebrow')}
      </span>
      <span aria-hidden className="font-display select-none leading-none mt-1"
            style={{ fontSize: big ? '5.5rem' : '2.4rem', letterSpacing: '-0.04em' }}>
        {String(ep?.number || '').padStart(2, '0')}
      </span>
    </div>
  );
};

const ArchiveSkeleton = () => (
  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
    {[0, 1].map(i => (
      <li key={i}
          className="h-28 rounded-2xl animate-pulse"
          style={{ background: 'rgba(251,251,250,0.55)',
                   boxShadow: '0 1px 0 rgba(43,65,56,0.04), 0 4px 14px rgba(43,65,56,0.04)' }} />
    ))}
  </ul>
);

/* ─────────────────────────────────────────────────────────────────
 * Single episode · fetches by slug and renders the section list via
 * <SectionRenderer>. Layout chrome stays the same as before; the
 * body comes from the API.
 * ──────────────────────────────────────────────────────────────── */
const NewsletterEpisode = ({ slug }) => {
  const t = useT();
  const { locale } = useLocale();
  const [ep, setEp] = useState(undefined);  // undefined = loading, null = not found
  useEffect(() => {
    setEp(undefined);
    api.newsletter(slug)
      .then(setEp)
      .catch(() => setEp(null));
  }, [slug]);
  // Log one view per (user, slug, locale) mount · fire-and-forget so a
  // failing ping never blocks the read. Re-fires if the reader switches
  // locale on the same page · that's intentional, it tells HR which
  // language people are actually reading in.
  useEffect(() => {
    api.recordNewsletterView(slug, locale);
  }, [slug, locale]);

  if (ep === undefined) {
    return (
      <Shell>
        <div className="max-w-[600px] mt-10 animate-pulse">
          <div className="h-3 w-32 rounded mb-3" style={{ background: LT.green30 }} />
          <div className="h-8 w-2/3 rounded mb-3" style={{ background: LT.green30 }} />
          <div className="h-3 w-full rounded" style={{ background: LT.green30 }} />
        </div>
      </Shell>
    );
  }
  if (ep === null) {
    return (
      <Shell>
        <div className="max-w-[600px] mt-10 rounded-2xl p-6"
             style={{ background: LT.beigeLight, border: `1px dashed ${LT.green30}`, color: LT.green50 }}>
          {t('maneva.notfound')}{' '}
          <Link to="/resources/newsletters" style={{ color: LT.greenDeep }} className="underline">
            {t('maneva.notfound.cta')}
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <Link to="/resources/newsletters"
            className="inline-flex items-center gap-1.5 text-[13px] mb-4"
            style={{ color: LT.green50 }}>
        <ArrowLeft className="h-3.5 w-3.5" /> {t('maneva.back')}
      </Link>

      {/* Editors can open a draft here (the API lets them) · say so, loudly
          enough that a screenshot never passes for the published page. */}
      {ep.status === 'draft' && (
        <div className="max-w-[820px] mb-6 rounded-xl px-4 py-2.5 text-[13px] flex items-center gap-2"
             style={{ background: '#fef3e2', color: '#9c6515', border: '1px solid #f3dcb0' }}>
          <Eye className="h-3.5 w-3.5 shrink-0" /> {t('maneva.draftPreview')}
        </div>
      )}

      {(() => {
        const l = localizedEpisode(ep, locale);
        // a table of contents once the episode is long enough to need one
        const toc = (ep.sections || [])
          .map(sec => ({ id: sec.id, title: localizedSection(sec, locale).title }))
          .filter(x => (x.title || '').trim());
        return (
          <>
          <header className="max-w-[820px] mb-10">
            <div className="text-[10.5px] uppercase tracking-[0.16em] font-medium font-num"
                 style={{ color: LT.green50 }}>
              MANEVA · {t('maneva.episode')} {ep.number} · {l.date_label}
            </div>
            <h1 className="font-display text-[2rem] sm:text-[2.6rem] leading-[1.06] tracking-tight mt-3"
                style={{ color: LT.greenDeep }}>
              {l.title}
            </h1>
            {l.teaser && (
              <p className="mt-4 text-[15.5px] leading-relaxed" style={{ color: '#3a4f44' }}>
                {l.teaser}
              </p>
            )}
          </header>
          {toc.length >= 5 && (
            <nav aria-label={t('maneva.toc')} className="max-w-[820px] mb-10 rounded-2xl p-5"
                 style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
              <div className="text-[11px] uppercase tracking-[0.14em] font-medium mb-2" style={{ color: LT.green50 }}>
                {t('maneva.toc')}
              </div>
              <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-[14px] list-decimal pl-5 marker:text-[hsl(var(--text-muted))]">
                {toc.map(x => (
                  <li key={x.id} className="pl-1">
                    <a href={`#s-${x.id}`} className="hover:underline" style={{ color: LT.greenDeep }}>{x.title}</a>
                  </li>
                ))}
              </ol>
            </nav>
          )}
          </>
        );
      })()}

      <article className="max-w-[820px] space-y-12">
        {(ep.sections || []).map((sec) => (
          <div key={sec.id} id={`s-${sec.id}`} style={{ scrollMarginTop: 96 }}>
            <SectionRenderer section={sec} locale={locale} />
          </div>
        ))}
      </article>

      {(ep.neighbors?.older || ep.neighbors?.newer) && (
        <nav className="mt-12 max-w-[820px] grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ep.neighbors.older ? <NeighborLink ep={ep.neighbors.older} dir="older" locale={locale} t={t} /> : <span />}
          {ep.neighbors.newer && <NeighborLink ep={ep.neighbors.newer} dir="newer" locale={locale} t={t} />}
        </nav>
      )}

      <div className="mt-16 mb-12 max-w-[820px] pt-6 border-t" style={{ borderColor: LT.green30 }}>
        <Link to="/resources/newsletters"
              className="inline-flex items-center gap-1.5 text-[13px]"
              style={{ color: LT.green50 }}>
          <ArrowLeft className="h-3.5 w-3.5" /> {t('maneva.back')}
        </Link>
      </div>
    </Shell>
  );
};

/* ─────────────────────────────────────────────────────────────────
 * Section renderer · dispatches by section.type. Markdown is parsed
 * inline (small subset: paragraphs, ### h3, **bold**, *italic*,
 * [text](url)) and rendered through the matching helper component.
 * ──────────────────────────────────────────────────────────────── */
export const SectionRenderer = ({ section: s, locale }) => {
  const galleryItems = mergeGalleryImages(s);
  // Resolve the per-locale copy once · the `title` and `body_markdown`
  // can both vary between FR / NL / EN, while `emoji` / `data` are
  // shared. `locale` is optional so admin previews that pass the raw
  // section still see the canonical FR content.
  const { title, body_markdown, items } = localizedSection(s, locale || 'fr');

  switch (s.type) {
    case 'h3':
      return <H3>{title}</H3>;

    case 'bullets':
      return (
        <Section emoji={s.emoji} title={title}>
          {renderBody(body_markdown)}
          <Bullets items={items.map((it, i) => <span key={i}>{renderInline(it)}</span>)} />
          {galleryItems.length > 0 && <Gallery items={galleryItems} />}
        </Section>
      );

    case 'gallery':
      return (
        <Section emoji={s.emoji} title={title}>
          {renderBody(body_markdown)}
          {galleryItems.length > 0 && <Gallery items={galleryItems} />}
        </Section>
      );

    case 'fin_table': {
      const { headers = [], rows = [], footer = null } = s.data || {};
      return (
        <Section emoji={s.emoji} title={title}>
          {renderBody(body_markdown)}
          <FinTable headers={headers} rows={rows} footer={footer} />
        </Section>
      );
    }

    case 'clue':
      return (
        <Section emoji={s.emoji} title={title}>
          <Clue label={s.data?.label || title || ''}>
            {renderInline(body_markdown || '')}
          </Clue>
        </Section>
      );

    case 'quote':
      return (
        <Section emoji={s.emoji} title={title}>
          <blockquote className="italic text-[15px] leading-relaxed pl-4 border-l-2"
                      style={{ color: '#3a4f44', borderColor: LT.green30 }}>
            {renderInline(body_markdown || '')}
          </blockquote>
        </Section>
      );

    case 'text':
    default:
      return (
        <Section emoji={s.emoji} title={title}>
          {renderBody(body_markdown)}
          {galleryItems.length > 0 && <Gallery items={galleryItems} />}
        </Section>
      );
  }
};

/* Pull gallery sources from both NewsletterSectionImage rows (uploaded
   via the editor) and `data.image_urls` (static URLs from the seed).
   Each item carries `{src, kind: 'image' | 'video'}` so the renderer
   can pick the right tag. Seeded URLs default to image (legacy data). */
/* Linked media (data.image_urls) carries no kind · a LampTwist Drive file
 * link ends in the file name, so the extension says video or image. */
export function mediaKindFromUrl(url) {
  return /\.(mp4|m4v|webm|mov)(?:[?#]|$)/i.test(url || '') ? 'video' : 'image';
}

function mergeGalleryImages(section) {
  const uploaded = (section.images || []).map(img => ({
    src: img.image,
    kind: img.kind || 'image',
  }));
  const seeded = (section.data?.image_urls || []).map(src => ({ src, kind: mediaKindFromUrl(src) }));
  return [...uploaded, ...seeded];
}

/* ─────────────────────────────────────────────────────────────────
 * Minimal markdown subset · paragraphs split on \n\n, `### heading`
 * → H3, plus inline **bold** / *italic* / [text](url). Keeps the
 * dependency footprint at zero for the small format we use.
 * ──────────────────────────────────────────────────────────────── */
export function renderBody(markdown) {
  if (!markdown) return null;
  const blocks = markdown.split(/\n{2,}/);
  return blocks.map((block, i) => {
    const trimmed = block.trim();
    if (!trimmed) return null;
    // Headings · ##  → bigger section heading (h2-ish), ###  → h3.
    // The order matters: test the longer prefix first so `### …`
    // doesn't accidentally match the `## ` rule.
    if (trimmed.startsWith('### ')) {
      return <H3 key={i}>{renderInline(trimmed.slice(4))}</H3>;
    }
    if (trimmed.startsWith('## ')) {
      return (
        <h2 key={i} className="font-display text-[1.35rem] mt-5 mb-1"
            style={{ color: LT.greenDeep }}>
          {renderInline(trimmed.slice(3))}
        </h2>
      );
    }
    // Bullet list · a block where every line is `- …` (or `* …`)
    // becomes a <Bullets>. Items keep their inline markdown.
    const lines = trimmed.split('\n');
    if (lines.length > 0 && lines.every(l => /^[-*]\s+/.test(l))) {
      const items = lines.map((l, j) =>
        <span key={j}>{renderInline(l.replace(/^[-*]\s+/, ''))}</span>
      );
      return <Bullets key={i} items={items} />;
    }
    return <P key={i}>{renderInline(trimmed)}</P>;
  });
}

export function renderInline(text, keyPrefix = 'n') {
  if (!text) return null;
  // Order alternatives so links match before plain * patterns at any
  // position where a link starts. Bold and italic recurse into their
  // inner content so [text](url) sitting *inside* **bold** is still
  // rendered as a link instead of being swallowed as plain text.
  const re = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  const out = [];
  let last = 0, m, i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const k = `${keyPrefix}-${i++}`;
    if (m[1] !== undefined) {
      out.push(
        <a key={k} href={m[2]} target="_blank" rel="noreferrer"
           className="underline" style={{ color: LT.greenDeep }}>{m[1]}</a>
      );
    } else if (m[3] !== undefined) {
      out.push(<strong key={k}>{renderInline(m[3], `${k}b`)}</strong>);
    } else if (m[4] !== undefined) {
      out.push(<em key={k}>{renderInline(m[4], `${k}i`)}</em>);
    }
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/* ─────────────────────────────────────────────────────────────────
 * Helper components · the building blocks the renderer uses. Each
 * one is the brand-styled equivalent of one Notion-export block type.
 * Kept exported so the editor preview reuses them verbatim.
 * ──────────────────────────────────────────────────────────────── */
export const Section = ({ emoji, title, children }) => (
  <section>
    {(emoji || title) && (
      <h2 className="font-display text-[1.4rem] sm:text-[1.55rem] leading-tight mb-4 flex items-baseline gap-2"
          style={{ color: LT.greenDeep }}>
        {emoji && <span className="text-[1.3rem]">{emoji}</span>}
        {title && <span>{title}</span>}
      </h2>
    )}
    <div className="space-y-3">{children}</div>
  </section>
);

export const P = ({ children }) => (
  <p className="text-[15px] leading-relaxed" style={{ color: '#3a4f44' }}>{children}</p>
);

export const H3 = ({ children }) => (
  <h3 className="font-display text-[1.15rem] mt-4" style={{ color: LT.greenDeep }}>{children}</h3>
);

export const Bullets = ({ items }) => (
  // pl-7 (instead of pl-5) gives the list a clearer indent off the
  // body text · the marker:mr-2 wedge adds breathing room between
  // the dot and the item content so a long line wraps under the
  // text, not under the bullet.
  <ul className="list-disc pl-7 space-y-2 text-[15px] leading-relaxed marker:text-[hsl(var(--text-muted))]"
      style={{ color: '#3a4f44' }}>
    {items.map((it, i) => (
      <li key={i} className="pl-1">{it}</li>
    ))}
  </ul>
);

export const Gallery = ({ items }) => (
  <div className={`mt-2 grid gap-3 ${items.length > 1 ? 'sm:grid-cols-2' : ''}`}>
    {items.map(item => {
      // `item` is the new {src, kind} shape; tolerate the legacy
      // string-only form so any caller that still passes raw URLs
      // (e.g. tests, older callers) keeps working.
      const src = typeof item === 'string' ? item : item.src;
      const kind = typeof item === 'string' ? 'image' : (item.kind || 'image');
      const className = 'w-full rounded-xl';
      const style = { border: `1px solid ${LT.green30}` };
      return kind === 'video' ? (
        <video key={src} src={src} controls preload="metadata" playsInline
               className={className} style={style} />
      ) : (
        <img key={src} src={src} alt="" className={className} style={style} />
      );
    })}
  </div>
);

export const Clue = ({ label, children }) => (
  <div className="rounded-xl p-4"
       style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
    <div className="text-[11px] uppercase tracking-[0.14em] font-medium mb-1"
         style={{ color: LT.green50 }}>
      {label}
    </div>
    <p className="text-[14.5px] leading-relaxed" style={{ color: '#3a4f44' }}>{children}</p>
  </div>
);

export const FinTable = ({ headers, rows: rawRows, footer }) => {
  // blank rows are kept while typing in the editor · never rendered
  const rows = (rawRows || []).filter(r => Array.isArray(r) && r.some(c => (c || '').trim() !== ''));
  return (
  <div className="overflow-x-auto mt-3 rounded-xl"
       style={{ border: `1px solid ${LT.green30}` }}>
    <table className="w-full font-num text-[13px]" style={{ color: '#2d3a32' }}>
      <thead>
        <tr style={{ background: LT.beigeLight, color: LT.greenDeep }}>
          {headers.map((h, i) => (
            <th key={i}
                className="px-3 py-2 text-left font-medium text-[11.5px] uppercase tracking-[0.08em] whitespace-nowrap"
                style={{ borderBottom: `1px solid ${LT.green30}` }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, ri) => (
          <tr key={ri} style={{ borderTop: ri === 0 ? 'none' : `1px solid ${LT.green30}` }}>
            {r.map((c, ci) => (
              <td key={ci}
                  className="px-3 py-2 align-top whitespace-nowrap"
                  style={{ fontWeight: ci === 0 ? 500 : 400 }}>
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
      {footer && (
        <tfoot>
          <tr style={{ background: LT.beigeLight, color: LT.greenDeep, borderTop: `1px solid ${LT.green30}` }}>
            {footer.map((c, ci) => (
              <td key={ci} className="px-3 py-2 whitespace-nowrap font-medium">{c}</td>
            ))}
          </tr>
        </tfoot>
      )}
    </table>
  </div>
  );
};
